import { useReducer, useCallback } from 'react';
import { usePostMessage } from '../../context/messageContext';
import { useTestContext } from '../../context/testContext';
import { useDotPosition } from '../visual-field-two-hooks/useDotPosition';

const boundaries = {
  x: window.screen.width,
  y: window.screen.height,
};

const centerScreen = {
  x: boundaries['x'] / 2,
  y: boundaries['y'] / 2,
};

export const useVisualFieldData = () => {
  const { testData } = useTestContext();
  const getCoordinates = useDotPosition();

  const fixationSize = {
    x: testData?.visualFieldSettings?.fixationTargetDimensions?.width,
    y: testData?.visualFieldSettings?.fixationTargetDimensions?.height,
  };

  const centeredPosition = { x: centerScreen.x - fixationSize.x / 2, y: centerScreen.y - fixationSize.y / 2 };

  const initialState = {
    dotsForDisplay: [],
    outOfBoundariesDots: [],
    seenDots: [],
    missedOnceDots: [],
    notSeenDots: [],
    activeDot: null,
    testEnded: false,
    testConfidence: 100,
    fixationTargetCoordinates: { x: centeredPosition.x, y: centeredPosition.y },
    stimulusCoordinates: { x: 0, y: 0 },
    fixationMoving: false,
    readyForNextStimulus: false,
    patientHasResponded: false,
    repeatThreshold: 0,
    dotsBetweenRepeats: 0,
    totalRepeats: 0,
    dotsSinceLastRepeat: 0,
    repeatCycleActive: false,
    totalBlindSpotsShown: 0,
  };

  const initialiseAllValues = () => {
    return initialState;
  };

  const visualFieldReducer = (existingData, action) => {
    const {
      dotsForDisplay,
      seenDots,
      missedOnceDots,
      notSeenDots,
      testConfidence,
      fixationTargetCoordinates,
      repeatThreshold,
      repeatCycleActive,
      dotsSinceLastRepeat,
      dotsBetweenRepeats,
      totalRepeats,
      totalBlindSpotsShown,
    } = existingData;

    switch (action.type) {
      case 'addSeenDot':
        return { ...existingData, seenDots: [...seenDots, action.payload], repeatCycleActive: seenDots.length >= repeatThreshold - 1 };
      case 'addMissedOnceDot':
        return { ...existingData, missedOnceDots: [...missedOnceDots, action.payload] };
      case 'addNotSeenDot':
        return { ...existingData, notSeenDots: [...notSeenDots, action.payload] };
      case 'setDotsForDisplay':
        const repeatDotThreshold = action.payload.length <= 10 ? 3 : 5;
        const repeatDotFrequency = Math.floor((action.payload.length - repeatDotThreshold) / repeatDotThreshold);
        return {
          ...existingData,
          dotsForDisplay: action.payload,
          repeatThreshold: repeatDotThreshold,
          dotsBetweenRepeats: repeatDotFrequency,
          dotsSinceLastRepeat: repeatDotFrequency,
        };
      case 'removeDotFromDisplay':
        return { ...existingData, dotsForDisplay: dotsForDisplay.filter((dot) => dot.id !== action.payload.id) };
      case 'setOutOfBoundariesDots':
        return { ...existingData, outOfBoundariesDots: action.payload };
      case 'removeFromMissedOnceArray':
        const newMissedOnceArray = [...missedOnceDots];
        newMissedOnceArray.splice(action.payload, 1);
        return { ...existingData, missedOnceDots: [...newMissedOnceArray] };
      case 'decreaseTestConfidence':
        if (testConfidence > 0) {
          return { ...existingData, testConfidence: testConfidence - 10 };
        } else return { ...existingData, testEnded: true };
      case 'setActiveDot':
        if (!dotsForDisplay.length) return { ...existingData, testEnded: true };
        const { newStimulusCoords, newFixationCoords } = getCoordinates({
          dot: action.payload,
          fixationTargetCoordinates,
        });

        const fixationTargetWillMove = newFixationCoords.x !== fixationTargetCoordinates.x || newFixationCoords.y !== fixationTargetCoordinates.y;

        return {
          ...existingData,
          fixationMoving: fixationTargetWillMove,
          readyForNextStimulus: !fixationTargetWillMove,
          activeDot: action.payload,
          stimulusCoordinates: newStimulusCoords,
          fixationTargetCoordinates: newFixationCoords,
        };
      case 'destroyActiveDot':
        const shouldIncrementRepeatCount = repeatCycleActive && dotsSinceLastRepeat < dotsBetweenRepeats;
        const shouldResetRepeatCount = repeatCycleActive && totalRepeats > 0 && dotsBetweenRepeats === dotsSinceLastRepeat;
        return {
          ...existingData,
          activeDot: null,
          readyForNextStimulus: false,
          patientHasResponded: false,
          dotsSinceLastRepeat: shouldIncrementRepeatCount ? dotsSinceLastRepeat + 1 : shouldResetRepeatCount ? 0 : dotsBetweenRepeats,
        };
      case 'incrementRepeatedDotCount':
        return { ...existingData, totalRepeats: totalRepeats + 1 };
      case 'setTestEnded':
        return { ...existingData, testEnded: true, fixationTargetCoordinates: { x: centeredPosition.x, y: centeredPosition.y } };
      case 'setFixationMoving':
        return { ...existingData, fixationMoving: action.payload };
      case 'setReadyForNextStimulus':
        return { ...existingData, readyForNextStimulus: action.payload };
      case 'setPatientHasResponded':
        return { ...existingData, patientHasResponded: action.payload };
      case 'handleBlindSpot':
        return {
          ...existingData,
          ...(totalBlindSpotsShown === 4 && { dotsForDisplay: dotsForDisplay.filter((dot) => !dot.blindspot) }),
          totalBlindSpotsShown: totalBlindSpotsShown + 1,
        };
      case 'reset':
        return initialiseAllValues();
      default:
        return existingData;
    }
  };

  const [testProgressData, updateTestProgressData] = useReducer(visualFieldReducer, initialState, initialiseAllValues);
  const postMessage = usePostMessage();

  const batchMultipleUpdates = useCallback(
    (updates) => {
      for (const action in updates) {
        postMessage({ type: 'useVisualFieldReducer', action: action, payload: updates[action] });
      }
    },
    [postMessage],
  );

  return { testProgressData, updateTestProgressData, batchMultipleUpdates };
};

export default useVisualFieldData;
